import initAxios from '@/shared/plugins/axios';
import axiosGlobal from '@/shared/plugins/axios-global';
import { configEnv } from '@/shared/config-env';

// @ts-ignore
import VueKeyCloak from '@dsb-norge/vue-keycloak-js/dist/dsb-vue-keycloak.es';
import {
  useViewAsOwner,
} from '@/shared/composition';
import emitter from '@/shared/services/emitter';
import { userHasPhoneGuard } from '@/shared/guards/userHasPhoneGuard';
import '@/app/repositories/registration';
import {
  createApp,
  h,
} from 'vue';
import axios from 'axios';
import 'vuetify/styles';
import { createVuetify } from 'vuetify';
import {
  VDialog,
  VCard,
  VCardTitle,
  VCardText,
  VBtn,
  VOverlay,
  VCardActions,
  VSpacer,
  VSnackbar,
  VIcon,
  VApp,
  VBadge,
  VAutocomplete,
  VSelect,
  VDataTable,
  VTextarea,
  VTextField,
  VExpansionPanels,
  VExpansionPanel,
  VExpansionPanelTitle,
  VExpansionPanelText,
  VAvatar,
  VCheckbox,
  VMenu,
  VForm,
  VCol,
  VContainer,
  VRow,
  VImg,
  VCombobox,
  VOtpInput,
  VProgressCircular,
  VProgressLinear,
  VSkeletonLoader,
  VTabs,
  VTab,
  VChip,
  VDivider,
  VAlert,
  VDatePicker,
  VSlider,
  VDataTableVirtual,
} from 'vuetify/components';
import {
  Intersect,
  ClickOutside,
} from 'vuetify/directives';
import { ru } from 'vuetify/locale';
import { useProjectSpecific } from '@/shared/mixins/useProjectSpecific';
import { useStore } from '@/shared/composition/useStore';
import { useMoment } from '@/shared/composition/useMoment';
import { createPinia } from 'pinia';
import { useUserStore } from '@/shared/store/useUserStore';
import { commonFormatPrice } from '@/shared/lib/domains/common/commonFormatPrice';
import { clipboardMixin } from '@/shared/lib/core/clipboard';
import { keycloak } from '@/shared/keycloak';
import { invert } from 'lodash-es';
import {
  projectName,
  projectNames,
} from '@/shared/constants/system';
import { callAfterAppReady } from '@/callAfterAppReady';
import { useSentryAsync } from '@/shared/composition/useSentryAsync';
import { handleRequestError } from '@/shared/api/handleApiError';
import { keycloakSource } from '@/bootstrap';

const { initViewAsOwner, ownerEmail } = useViewAsOwner();
const { useSentry } = useSentryAsync();

userHasPhoneGuard();
const projectSpecifics = useProjectSpecific();
const projectNamesValues = invert(projectNames);

export function initApp(App: any, router: any, i18n: any, keyloakClientId: any, mountId: string = '#app') {
  const url = new URLSearchParams(location.search);
  if (url.has('href')) {
    setTimeout(() => {
      const href = url.get('href')?.replace('my', '')
        .replace('//', '/');
      router.push(href);
    }, 200);
  }

  router.afterEach((to: any, from: any) => {
    document.body.classList.remove(from.name);
    document.body.classList.add(to.name);
  });

  function protectRoutes() {
    router.beforeEach((_to: any, _from: any, next: any) => {
      keycloak.value.authenticated ? next() : authenticate();
    });
  }

  function authenticate() {
    if (keycloak.value.authenticated) return;
    window.location.replace(keycloak.value.createLoginUrl());
  }

  function initKeycloak(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!keycloak.value.authenticated) reject(Error('401'));
      else {
        protectRoutes();
        resolve();
      }
    });
  }

  return new Promise(resolve => {
    const vuetify = createVuetify({
      components: {
        VDialog,
        VCard,
        VCardTitle,
        VCardText,
        VBtn,
        VOverlay,
        VCardActions,
        VSpacer,
        VSnackbar,
        VIcon,
        VApp,
        VBadge,
        VAutocomplete,
        VSelect,
        VDataTable,
        VTextarea,
        VTextField,
        VExpansionPanels,
        VExpansionPanel,
        VExpansionPanelTitle,
        VExpansionPanelText,
        VAvatar,
        VCheckbox,
        VMenu,
        VForm,
        VCol,
        VContainer,
        VRow,
        VImg,
        VCombobox,
        VOtpInput,
        VProgressCircular,
        VProgressLinear,
        VSkeletonLoader,
        VTabs,
        VTab,
        VChip,
        VDivider,
        VAlert,
        VDatePicker,
        VSlider,
        VDataTableVirtual,
      },
      directives: {
        Intersect,
        ClickOutside,
      },
      locale: {
        locale: 'ru',
        fallback: 'ru',
        messages: { ru },
      },
    });

    router.onError((err: any) => {
      if (err.message.indexOf('LoadError')) {
        emitter.emit('openInfoDialog', {
          title: 'Ошибка',
          text: 'Страница устарела, пожалуйста обновите',
          cancelText: 'обновить',
          afterCloseByButton() {
            window.location.reload();
          },
        });
      }
    });

    const pinia = createPinia();
    const appInstance = createApp({
      render: () => h(App),
    })
      .use({
        install(app) {
          const onReady = (kc: any) => {
            keycloak.value = kc;
            keycloakSource.receive(kc);
            (window as any).keycloak = kc;

            initKeycloak()
              .then(() => initAxios(kc))
              .then(() => setToken(kc.token))
              .then(async () => {
                useSentry(appInstance, router, kc);
                const userStore = useUserStore();
                initViewAsOwner();

                if (ownerEmail.value) {
                  userStore.setUserEmailFromKC(ownerEmail.value);
                }

                try {
                  await userStore.fetchPhysicalUserCard();
                } catch (e) {
                  handleRequestError(e);
                }

                appInstance.mount(mountId);
              })
              .catch(authenticate);

            callAfterAppReady(() => {
              const userStore = useUserStore();
              userStore.setUserEmailFromKC(kc.tokenParsed?.email ?? '');
              userStore.setUserNameFromKC({
                firstName: kc.tokenParsed?.given_name ?? '',
                lastName: kc.tokenParsed?.family_name ?? '',
              });
            });

            resolve(1);
          };

          if (configEnv.DISABLE_KEYCLOAK) {
            onReady({
              isSynthetic: true,
              authenticated: true,
              token: 'disabled_keycloak',
              timeSkew: 0,
              tokenParsed: {
                given_name: 'kk disabled',
                family_name: 'kk disabled',
                email: 'disabled@keycloak.com',
                exp: new Date().getTime() + 99999999,
              },
              updateToken: () => Promise.resolve(true),
              ...(configEnv.FAKE_KEYCLOAK || {}),
            });
            return;
          }

          app
            .use(VueKeyCloak, {
              config: {
                url: configEnv.SSO_URL + '/auth',
                realm: configEnv.SSO_REALM,
                clientId: keyloakClientId,
              },
              init: {
                onLoad: 'login-required',
                checkLoginIframe: false,
              },
              onReady,
            });
        },
      })
      .use(pinia)
      .use(router)
      .use(i18n)
      .use(vuetify)
      .mixin({
        computed: {
          isMTI() {
            return projectName === projectNames.MTI;
          },
          isSF() {
            return projectName === projectNames.SF;
          },
          isMOSAP() {
            return projectName === projectNames.MOSAP;
          },
          isPEOPLE() {
            return projectName === projectNames.PEOPLE;
          },
          $moment() {
            return useMoment();
          },
          $projectName() {
            return projectSpecifics.projectName;
          },
          $projectNames() {
            return projectSpecifics.projectNames;
          },
          $projectSpecificResources() {
            return projectSpecifics.projectSpecificResources;
          },
          $projectResources() {
            return projectSpecifics.projectSpecificResources;
          },
          $projectStyleVariables() {
            return projectSpecifics.projectStyleVariables;
          },
          $store() {
            return useStore();
          },
          $i18n() {
            return i18n.global;
          },
          $listeners() {
            return {};
          },
        },
        methods: {
          openRoute(path: string | { name: string }) {
            if (this.$route.path !== path) {
              this.$router.push(path);
            }
          },
          adaptedSlots() {
            return this.$scopedSlots;
          },
          moment(value: string, format = 'DD.MM.YYYY') {
            return this.$moment(value).format(format);
          },
          formatPrice: commonFormatPrice,
          $copyText: clipboardMixin().copyText,
          cssClass(this: any, forAll: Record<string, boolean>, forProjects: any) {
            const projectNameKey = projectNamesValues[projectName as any] as any;
            let projectsCss = forProjects[projectNameKey] ?? '';

            if (forProjects.DEFAULT && projectsCss === '') {
              projectsCss = forProjects.DEFAULT;
            }

            return {
              ...forAll,
              [projectsCss]: true,
            };
          },
        },
      })
      ;
  });
}

function setToken(token: string | undefined) {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    axiosGlobal.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
}
